import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseServices } from 'src/app/kuba/kuba.services';
import { CacheHandlerService } from 'src/app/kuba/cached.services';
import { environment } from 'src/environments/environment';

interface PortalBusinessRow {
  businessId: number;
  businessName: string;
  percentage: number;
  smiley: 'red' | 'yellow' | 'green';
  hseAdvisorName: string | null;
  completedOn: string | null;
}

interface PortalSummary {
  totalCount: number;
  greenCount: number;
  yellowCount: number;
  redCount: number;
  businesses: PortalBusinessRow[];
}

/**
 * Full-page portal list of businesses with a completed BHT-samsvar review.
 * Reached from the dashboard pie chart (slice click carries the bucket as
 * <c>?filter=green|yellow|red</c>). Columns: business name, current score,
 * assigned HSE advisor, and an R-mode button that opens the business's
 * Flexi compliance page directly.
 *
 * This is intentionally separate from the admin business list — it is a
 * compliance lens over the portal's businesses, not business management.
 */
@Component({
  selector: 'app-flexi-compliance-portal-list',
  templateUrl: './flexi-compliance-portal-list.component.html',
  styleUrls: ['./flexi-compliance-portal-list.component.scss'],
  standalone: false
})
export class FlexiCompliancePortalListComponent implements OnInit {
  loading = true;
  summary: PortalSummary | null = null;
  activeFilter: 'red' | 'yellow' | 'green' | null = null;

  private remoteWindow: Window | null = null;

  constructor(
    private http: HttpClient,
    private route: ActivatedRoute,
    private router: Router,
    private cacheHandlerService: CacheHandlerService
  ) {}

  ngOnInit(): void {
    const filterParam = this.route.snapshot.queryParamMap.get('filter');
    if (filterParam === 'red' || filterParam === 'yellow' || filterParam === 'green') {
      this.activeFilter = filterParam;
    }

    const url = `${environment.BASE_URL}/flexi/compliance/portal/${BaseServices.UserId}/summary`;
    this.http.get<any>(url).subscribe({
      next: data => {
        this.summary = this.normalizeSummary(data);
        this.loading = false;
      },
      error: () => {
        this.summary = this.emptySummary();
        this.loading = false;
      }
    });
  }

  /** Case-tolerant payload mapper with hard defaults — `businesses` must never be undefined. */
  private normalizeSummary(r: any): PortalSummary {
    if (!r) return this.emptySummary();
    const rows = (r.businesses ?? r.Businesses ?? []) as any[];
    return {
      totalCount: r.totalCount ?? r.TotalCount ?? rows.length ?? 0,
      greenCount: r.greenCount ?? r.GreenCount ?? 0,
      yellowCount: r.yellowCount ?? r.YellowCount ?? 0,
      redCount: r.redCount ?? r.RedCount ?? 0,
      businesses: rows.map(b => ({
        businessId: b.businessId ?? b.BusinessId,
        businessName: b.businessName ?? b.BusinessName ?? '',
        percentage: Number(b.percentage ?? b.Percentage ?? 0),
        smiley: (b.smiley ?? b.Smiley ?? 'red') as 'red' | 'yellow' | 'green',
        hseAdvisorName: b.hseAdvisorName ?? b.HseAdvisorName ?? null,
        completedOn: b.completedOn ?? b.CompletedOn ?? null
      }))
    };
  }

  get filteredRows(): PortalBusinessRow[] {
    const rows = this.summary?.businesses ?? [];
    if (this.activeFilter === null) return rows;
    return rows.filter(b => b.smiley === this.activeFilter);
  }

  setFilter(slice: 'red' | 'yellow' | 'green' | null): void {
    this.activeFilter = this.activeFilter === slice ? null : slice;
    // Keep the URL shareable — the filter survives refresh / copy-paste.
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: this.activeFilter ? { filter: this.activeFilter } : {},
      replaceUrl: true
    });
  }

  backToDashboard(): void {
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  /**
   * R-mode jump: same sessionStorage shim the other portal widgets use —
   * opens the business's Flexi compliance page in a dedicated window,
   * reusing one window across clicks.
   */
  openFlexi(row: PortalBusinessRow): void {
    if (this.remoteWindow) this.remoteWindow.close();
    this.cacheHandlerService.clearCachedData();
    sessionStorage.setItem('RModeBusiness', JSON.stringify({ BusinessName: row.businessName }));
    sessionStorage.setItem('RModeBusinessId', row.businessId.toString());

    const baseUrl = BaseServices.userBase?.BaseUrl ?? '';
    const url = `${window.location.origin}/${baseUrl}/app/business/${row.businessId}/Flexi/compliance`;
    const features = `location=yes,width=${screen.availWidth},height=${screen.availHeight},scrollbars=yes,status=yes`;
    this.remoteWindow = window.open(url, 'Remote', features);
  }

  smileyClass(smiley: string): string { return `is-${smiley}`; }

  smileyLabelKey(smiley: string): string {
    switch (smiley) {
      case 'green': return 'FLEXI_COMPLIANCE.SCORE_CHIP.GREEN_LABEL';
      case 'yellow': return 'FLEXI_COMPLIANCE.SCORE_CHIP.YELLOW_LABEL';
      default: return 'FLEXI_COMPLIANCE.SCORE_CHIP.RED_LABEL';
    }
  }

  private emptySummary(): PortalSummary {
    return { totalCount: 0, greenCount: 0, yellowCount: 0, redCount: 0, businesses: [] };
  }
}
